import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { HabitNumericRecord } from '@app/_interfaces/habit.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { toDate } from '@app/_services/date/date.functions';
import { SettingsService } from '@app/_services/settings.service';
import { getNumStrId } from '@app/_utils/utils';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { InputTimePickerComponent, InputTimePickerModel } from '@app/components/shared/inputs/input-time-picker/input-time-picker.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { Subject, takeUntil } from 'rxjs';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-habit-numeric-value',
  imports: [
    CommonModule,
    SvgComponent,
    InputTextComponent
  ],
  templateUrl: './habit-numeric-value.component.html',
  styleUrl: './habit-numeric-value.component.scss'
})

export class HabitNumericValueComponent {

  numericControl: FormControl = new FormControl(null, [Validators.required, Validators.maxLength(10)]);
  timestamp: FormControl = new FormControl(new Date());
  initialTimestamp: Date | null = new Date();
  unSubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<HabitNumericValueComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'new' | 'edit', value: HabitNumericRecord | null, unit: string },
    public cc: CacheService,
    private alertService: AlertService,
    public ss: SettingsService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.data.value) {
      this.numericControl.setValue(this.data.value.value ? String(this.data.value.value) : null);
      this.timestamp.setValue(this.data.value.recordedAt);
      this.initialTimestamp = this.data.value.recordedAt;
    };

    dialogRef.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  normalizeDate = (date: any): Date | null => {
    if (!date) return null;
    if (typeof date === 'object' && '_seconds' in date) return toDate(date);
    return new Date(date);
  };

  hasChanges(): boolean {
    const timestamp = this.normalizeDate(this.timestamp.value);
    const initialTimestamp = this.normalizeDate(this.initialTimestamp);

    const timestampChanged = timestamp?.getTime() !== initialTimestamp?.getTime();
    if (this.data.mode === 'new') {
      return this.numericControl.value !== null || timestampChanged;
    } else {
      return Number(this.numericControl.value) !== this.data.value?.value || timestampChanged;
    }
  }

  save() {
    const numericAnswer: HabitNumericRecord = {
      id: this.data.value ? this.data.value.id : getNumStrId(),
      value: Number(this.numericControl.value),
      recordedAt: this.timestamp.value
    };
    this.dialogRef.close({ type: this.data.mode === 'new' ? 'ADD' : 'EDIT', value: numericAnswer });
  }

  openTimePicker() {
    const currentDate: Date = this.timestamp.value || new Date();
    const timeStr: string = this.dateToCustomDate(currentDate);
    const dateStr: string = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

    const dialogRef = this.modalService.openModalLevel4(
      InputTimePickerComponent,
      new InputTimePickerModel(timeStr, true, true, false, false, dateStr),
      { disableClose: true }
    );

    this.modalService.getClosedObservable(dialogRef).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result && result.timeString) {
        // Build Date from dateString + timeString
        const [year, month, day] = result.dateString.split('-').map(Number);
        const [hours, minutes] = result.timeString.split(':').map(Number);
        this.timestamp.setValue(new Date(year, month - 1, day, hours, minutes, 0, 0));
        this.cdr.markForCheck();
      }
    });
  }

  private dateToCustomDate(date: Date): string {
    date = this.normalizeDate(date) as Date;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.t('saveChanges_title'), this.ss.t('common_discardContent'), this.ss.t('common_save'), this.ss.t('common_discard'), 'color-35', 'color-11');
      if (!res) return;
      if (res.confirm === true) {
        this.save();
      } else if (res.confirm === false) {
        this.dialogRef.close();
      }
    } else {
      this.dialogRef.close();
    }
  }
}
